import React from 'react';
import BlockContent from '@sanity/block-content-to-react';
import get from 'lodash/get';

import sanitizeArticleCallout from 'state/sanitizers/PortableTextModules/sanitizeArticleCallout';
import sanitizeArticleImage from 'state/sanitizers/PortableTextModules/sanitizeArticleImage';
import sanitizeArticleVideo from 'state/sanitizers/PortableTextModules/sanitizeArticleVideo';
import sanitizeImage from 'state/sanitizers/sanitizeImage';

import sanityImgUtil from 'utils/sanityImgUtil';
import sanityImgSrcSetUtil from 'utils/sanityImgSrcSetUtil';
import sanitizeInternalLink from 'utils/sanitizeInternalLink';

import {
  PortableTextButton,
  PortableTextLink,
  PortableTextH1,
  PortableTextH2,
  PortableTextH3,
  PortableTextH4,
} from 'styled';
import { Button, Link, Video, Img } from 'components/base';
import ArticleCalloutComponent from 'components/portableTextModules/ArticleCallout';
import ArticleImageComponent from 'components/portableTextModules/ArticleImage';

export const Blue = (props: unknown) => {
  const children = get(props, 'children', null);

  if (!children) {
    return null;
  }

  return <span className="color-blue">{children}</span>;
};

export const TextSmall = (props: unknown) => {
  const { style = 'normal' } = get(props, 'node');
  const children = get(props, 'children', null);

  if (!children) {
    return null;
  }

  if (style === 'smallText') {
    return <span className="text-article-sm">{children}</span>;
  }

  /* default to normal behavior */
  return BlockContent.defaultSerializers.types.block(props);
};

export const SansSerif = (props: unknown) => {
  const children = get(props, 'children', null);

  if (!children) {
    return null;
  }

  return <span className="text-sans-serif">{children}</span>;
};

export const ExternalLink = (props: unknown) => {
  const children = get(props, 'children', '');
  const href = get(props, 'mark.href', '');
  const sanitizedInternalLink = sanitizeInternalLink(href);
  const hrefIsRelative = sanitizedInternalLink.charAt(0) === '/';

  if (!children) {
    return null;
  }

  return (
    <PortableTextButton
      as={Button}
      to={sanitizedInternalLink}
      ariaLabel={children}
      openInCurrentTab={hrefIsRelative}
    >
      <span>{children}</span>
    </PortableTextButton>
  );
};

export const InternalLink = (props: unknown) => {
  const children = get(props, 'children', '');
  const href = get(props, 'mark.link', '');
  const sanitizedInternalLink = sanitizeInternalLink(href);

  if (!children) {
    return null;
  }

  return (
    <PortableTextLink as={Link} to={sanitizedInternalLink} ariaLabel={children}>
      <span>{children}</span>
    </PortableTextLink>
  );
};

export const Image = (props: unknown) => {
  if (!get(props, 'node')) {
    return null;
  }

  const image = sanitizeImage(get(props, 'node.asset'), get(props, 'node.alt'));

  return (
    <Img
      alt={image.alt}
      className="TextModule__image wauto"
      sizes="(maxWidth: 768px) 400px, (maxWidth: 1024px) 500px, 650px"
      src={sanityImgUtil(image, 400)}
      srcSet={sanityImgSrcSetUtil(image, 400, 500, 650)}
      dimensions={image.metadata?.dimensions}
      crop={image.crop}
      useOriginalImageSize
    />
  );
};

export const ArticleImage = (props: unknown) => {
  return (
    <ArticleImageComponent
      articleImage={sanitizeArticleImage(get(props, 'node'))}
    />
  );
};

export const ArticleCallout = (props: unknown) => {
  return (
    <ArticleCalloutComponent
      articleCallout={sanitizeArticleCallout(get(props, 'node'))}
    />
  );
};

export const ArticleVideo = (props: unknown) => {
  const sanitizedArticleVideo = sanitizeArticleVideo(get(props, 'node'));

  return (
    <Video
      video={sanitizedArticleVideo}
      isFullWidth={sanitizedArticleVideo?.isFullWidth}
      caption={sanitizedArticleVideo?.caption}
      isInsidePortableText
      showVideoControl
    />
  );
};

export const ListItem = (props: unknown) => {
  const children = get(props, 'children', null);

  if (!children) {
    return null;
  }

  return (
    <li>
      <span>{children}</span>
    </li>
  );
};

export const BlockRenderer = (props: unknown) => {
  const children = get(props, 'children', null);
  const style = get(props, 'node.style', 'normal');

  if (!children) {
    return null;
  }

  if (style === 'h1') {
    return <PortableTextH1>{children}</PortableTextH1>;
  }

  if (style === 'h2') {
    return <PortableTextH2>{children}</PortableTextH2>;
  }

  if (style === 'h3') {
    return <PortableTextH3>{children}</PortableTextH3>;
  }

  if (style === 'h4') {
    return <PortableTextH4>{children}</PortableTextH4>;
  }

  // Fall back to default handling
  return BlockContent.defaultSerializers.types.block(props);
};
